import { useState, type ReactNode } from 'react';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { alpha, useTheme, styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { BoxProps } from '@mui/material';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListSubheader,
  Tooltip
} from '@mui/material';
import typography from '../theme/typography';
import useLocales from 'hooks/useLocales';

const ListSubheaderStyle = styled((props: any) => (
  <ListSubheader disableSticky disableGutters {...props} />
))(({ theme }: { theme: Theme }) => ({
  ...typography.overline,
  fontSize: '0.65rem',
  letterSpacing: '0.08em',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1.25),
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: theme.palette.text.secondary
}));

const ListItemStyle = styled(ListItem)(({ theme }: any) => ({
  ...typography.body2,
  height: 32,
  position: 'relative',
  textTransform: 'capitalize',
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  marginBottom: 0,
  width: `calc(100% - ${theme.spacing(2)})`,
  borderRadius: 6,
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover
  },
  '&.active': {
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: alpha(theme.palette.primary.main, 0.08)
  }
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 20,
  height: 20,
  minWidth: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// `disableTypography` skips MUI's built-in text-truncation styling, so long
// titles (e.g. a circle name) wrap or overflow the fixed 32px row height and
// visually collide with the item below it — this restores single-line ellipsis.
const listItemTextSx = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0
} as const;

type NavItemProps = {
  title: string;
  path?: string;
  icon?: React.JSX.Element;
  info?: React.JSX.Element;
  defaultOpen?: boolean;
  navKey?: string;
  exact?: boolean;
  children?: {
    title: string;
    path?: string;
  }[];
};

function NavItem({ item, collapsed }: { item: NavItemProps; collapsed?: boolean }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { title, path, icon, info, children, defaultOpen, exact } = item;
  const isActiveRoot = path ? !!matchPath({ path, end: exact ?? false }, pathname) : false;
  const { translate } = useLocales();

  const isActiveChild =
    children?.some((child) =>
      child.path ? !!matchPath({ path: child.path, end: false }, pathname) : false
    ) || false;

  const [open, setOpen] = useState(defaultOpen || isActiveRoot || isActiveChild);

  const handleOpen = () => {
    setOpen(!open);
  };

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: alpha(theme.palette.primary.main, 0.08)
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  };

  // Collapsed: icon-only with tooltip, links to first child path if parent
  if (collapsed) {
    const targetPath = path || children?.[0]?.path;
    const isActive = isActiveRoot || isActiveChild;
    return (
      <Tooltip title={translate(title)} placement="right" arrow>
        <ListItemStyle
          disableGutters
          // @ts-ignore — RouterLink props are not inferred by the MUI polymorphic component
          component={RouterLink}
          to={targetPath}
          end={exact}
          sx={{
            justifyContent: 'center',
            px: 0,
            height: 32,
            width: 72,
            mx: 'auto',
            ...(isActive && activeRootStyle)
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {icon}
          </Box>
        </ListItemStyle>
      </Tooltip>
    );
  }

  if (children) {
    return (
      <>
        <ListItemStyle
          disableGutters
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={translate(title)} sx={listItemTextSx} />
          {info && info}
          {open ? (
            <KeyboardArrowDown sx={{ width: 16, height: 16, ml: 1, color: 'text.disabled' }} />
          ) : (
            <KeyboardArrowRight sx={{ width: 16, height: 16, ml: 1, color: 'text.disabled' }} />
          )}
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path } = item;
              const isActiveSub = path ? !!matchPath({ path, end: false }, pathname) : false;

              return (
                <ListItemStyle
                  disableGutters
                  key={title}
                  // @ts-ignore — RouterLink props are not inferred by the MUI polymorphic component
                  component={RouterLink}
                  to={path}
                  sx={{
                    pl: 2.5,
                    ...(isActiveSub && activeSubStyle)
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main'
                        })
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={translate(title)} sx={listItemTextSx} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      disableGutters
      // @ts-ignore — RouterLink props are not inferred by the MUI polymorphic component
      component={RouterLink}
      to={path}
      end={exact}
      sx={{
        ...(isActiveRoot && activeRootStyle)
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={translate(item.title)} sx={listItemTextSx} />
      {info && info}
    </ListItemStyle>
  );
}

interface NavSectionProps extends BoxProps {
  navConfig: {
    subheader: string;
    items: NavItemProps[];
  }[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  leadingAction?: ReactNode;
  subheaderBanners?: Partial<Record<string, ReactNode>>;
}

export default function NavSection({
  navConfig,
  collapsed,
  onToggleCollapse,
  leadingAction,
  subheaderBanners,
  ...other
}: NavSectionProps) {
  const { translate } = useLocales();
  return (
    <Box {...other}>
      {navConfig.length === 0 ? leadingAction : null}
      {navConfig.map((list, index) => {
        const { subheader, items } = list;
        const banner = !collapsed ? subheaderBanners?.[subheader] : undefined;
        return (
          <List key={subheader} disablePadding>
            {collapsed ? (
              index === 0 && onToggleCollapse ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 0.5 }}>
                  <Tooltip title={translate('menu.expand_sidebar')} placement="right">
                    <IconButton
                      size="small"
                      onClick={onToggleCollapse}
                      aria-label={translate('menu.expand_sidebar')}
                      aria-expanded={false}
                    >
                      <KeyboardDoubleArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : (
                <Divider sx={{ mx: 1.5, mt: index === 0 ? 1.5 : 2, mb: 0.5 }} />
              )
            ) : (
              <ListSubheaderStyle sx={index === 0 ? { mt: 0 } : undefined}>
                {translate(subheader)}
                {index === 0 && onToggleCollapse && (
                  <Tooltip title={translate('menu.collapse_sidebar')} placement="right">
                    <IconButton
                      size="small"
                      onClick={onToggleCollapse}
                      aria-label={translate('menu.collapse_sidebar')}
                      aria-expanded
                      sx={{ width: 28, height: 28, color: 'text.secondary' }}
                    >
                      <KeyboardDoubleArrowLeftIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </ListSubheaderStyle>
            )}
            {index === 0 && leadingAction ? (
              <Box sx={{ mb: 1, px: collapsed ? 0 : 1 }}>{leadingAction}</Box>
            ) : null}
            {banner ? <Box sx={{ px: 1.5, mb: 1, mt: -0.25 }}>{banner}</Box> : null}
            {items.map((item: NavItemProps) => (
              <NavItem key={item.navKey ?? item.title} item={item} collapsed={collapsed} />
            ))}
          </List>
        );
      })}
    </Box>
  );
}
